import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import dayjs from 'dayjs';
import {
    BarChart3,
    Check,
    Clock,
    DollarSign,
    FileCheck,
    FileText,
    Percent,
    RefreshCw,
    Sparkles,
    Send,
    Tag,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { httpClient } from '../../common/httpclient';
import { useAppBarAction, useAppBarSubtitle } from '../../component/layout/AppBarActions';
import { KitCard, SecH } from '../../component/common/MobileKit';
import { BarsChart, HBars, Kpi, KpiGrid, KitEmptyNote, ProgressBar, formatMoneyShort, parseMoney } from '../../component/common/MobileDashboardKit';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import { usePermissions } from '../../hooks/usePermissions';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import DesktopPeriodSelector from '../../component/common/DesktopPeriodSelector';
import KpiTickerCard, { KpiDeltaChip } from '../../component/report/KpiTickerCard';
import AnimatedNumber, { AnimatedMoney } from '../../component/common/AnimatedNumber';
import './dashboard.css';

// Card header (`.card-head`): 36px icon chip (tinted bg/fg) + title + one
// line subtitle (+ total opcional à direita, `.head-total`) — DESIGN-SYSTEM.md §5.2.
function CardHeadRow({ icon, iconBg, iconFg, title, subtitle, loading, right }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
            <Box sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: iconBg,
                color: iconFg,
            }}>
                {icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: 'text.primary' }}>
                    {title}
                </Typography>
                <Typography sx={{ fontSize: '11.5px', color: 'text.disabled', mt: '1px' }}>
                    {loading ? <Skeleton variant="text" width={140} /> : subtitle}
                </Typography>
            </Box>
            {right && <Box sx={{ ml: 'auto', textAlign: 'right', lineHeight: 1.1 }}>{right}</Box>}
        </Box>
    );
}

// `.summary-row` — label (icon chip + text) left, bold value right (igual ao hero do SaleDashboard).
function SummaryRow({ icon, iconBg, iconFg, label, value, valueColor, chip, loading }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1, gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <Box sx={{
                    width: 26,
                    height: 26,
                    borderRadius: '8px',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: iconBg,
                    color: iconFg,
                }}>
                    {icon}
                </Box>
                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                    {label}
                </Typography>
            </Box>
            {loading ? (
                <Skeleton variant="text" width={70} height={20} />
            ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {chip}
                    <Typography sx={{ fontSize: '15px', fontWeight: 800, color: valueColor || 'text.primary' }}>
                        {value}
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

// `.pay-row` — 32px icon chip + label com trilha proporcional + valor em negrito e
// "% do total". Mesmo formato do card "Status das Vendas" (SaleDashboard.js).
function StatusRow({ icon, iconBg, iconFg, label, value, barWidth, pctLabel, barColor, loading }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: '11px', '& + &': { borderTop: '1px dashed', borderColor: 'divider' } }}>
            <Box sx={{
                width: 32,
                height: 32,
                borderRadius: '9px',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: iconBg,
                color: iconFg,
            }}>
                {icon}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'text.secondary' }}>
                    {label}
                </Typography>
                <Box sx={{ height: 5, borderRadius: '4px', bgcolor: 'background.subtle', overflow: 'hidden' }}>
                    {!loading && <Box sx={{ height: '100%', borderRadius: '4px', width: `${barWidth}%`, bgcolor: barColor, transition: 'width .4s ease' }} />}
                </Box>
            </Box>
            {loading ? (
                <Skeleton variant="text" width={70} height={18} />
            ) : (
                <Box sx={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: '1px' }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 800, color: 'text.primary' }}>
                        {value}
                    </Typography>
                    <Typography sx={{ fontSize: '10.5px', fontWeight: 600, color: 'text.disabled' }}>
                        {pctLabel}
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

// `.chart-legend-row .li` — dot + label only (no value), used under the
// "Orçamentos x Ordens" line chart.
function ChartLegendItem({ color, label }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11.5px', color: 'text.secondary' }}>
            <Box sx={{ width: 9, height: 9, borderRadius: '3px', bgcolor: color, flexShrink: 0 }} />
            <Typography component="span" sx={{ fontSize: '11.5px', color: 'text.secondary' }}>{label}</Typography>
        </Box>
    );
}

// DESIGN-SYSTEM.md §5.11 empty state — centered dashed-circle icon + bold
// line + smaller instructional line. Local copy of `SaleDashboard.js`'s.
function EmptyMiniState({ icon, title, subtitle }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 3, textAlign: 'center' }}>
            <Box sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                border: '2px dashed',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.disabled',
            }}>
                {icon}
            </Box>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.secondary' }}>{title}</Typography>
            <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>{subtitle}</Typography>
        </Box>
    );
}

const DEFAULT_QUOTE_DATA = {
    total_quotes: 0,
    count_new: 0,
    count_awaiting_approval: 0,
    count_approved: 0,
    count_sent_to_client: 0,
    count_order_generated: 0,
    total_amount: '0,00',
    average_amount: '0,00',
    total_amount_with_discount: '0,00',
    conversion_rate: 0,
    chart_by_status: [],
    chart_by_day: []
};

const fmtPct = (num, den) => (den > 0 ? `${((num / den) * 100).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%` : '—');
const toCents = (raw) => Math.round(parseMoney(raw) * 100);
const toRate = (raw) => (typeof raw === 'number' ? raw : parseFloat(raw) || 0);
// Limite para casar `chart_by_day` (dia dd/MM, sem ano) com cada dia do período sem ambiguidade.
const MAX_SPARK_DAYS = 365;

export default function QuoteDashboard() {
    const theme = useTheme();
    const isMobileShell = useIsMobileShell();
    const { hasPermission } = usePermissions();
    // `/dashboard/quote/chart-quote-vs-order` exige `dashboard.servicequote` (fv-api/config/router.go);
    // `/dashboard/quote` aceita também `home.view`. Sem a permissão não chamamos o gráfico (evita 403).
    const canChart = hasPermission('dashboard.servicequote');
    const [loading, setLoading] = useState(true);
    const [quoteData, setQuoteData] = useState(DEFAULT_QUOTE_DATA);
    const [prevData, setPrevData] = useState(null);
    const [quoteVsOrderData, setQuoteVsOrderData] = useState([]);
    const [startDate, setStartDate] = useState(dayjs().startOf('month'));
    const [endDate, setEndDate] = useState(dayjs().endOf('month'));
    const requestId = useRef(0);

    // Desktop: período escolhido. Mobile não tem seletor de período, então
    // mantém o comportamento anterior (sem startDate/endDate).
    const periodQuery = (start, end) => (isMobileShell || !start?.isValid?.() || !end?.isValid?.()
        ? ''
        : `?startDate=${start.format('DD/MM/YYYY')}&endDate=${end.format('DD/MM/YYYY')}`);

    const fetchQuoteData = async (start, end) => {
        try {
            const response = await httpClient.get(`/dashboard/quote${periodQuery(start, end)}`);
            return response.data || DEFAULT_QUOTE_DATA;
        } catch (error) {
            console.error('Erro ao carregar dados dos orçamentos:', error);
            return DEFAULT_QUOTE_DATA;
        }
    };

    const fetchQuoteVsOrderData = async (start, end) => {
        if (!canChart) return [];
        try {
            const response = await httpClient.get(`/dashboard/quote/chart-quote-vs-order${periodQuery(start, end)}`);
            return response.data?.data || [];
        } catch (error) {
            console.error('Erro ao carregar dados de Orçamentos x Ordens:', error);
            return [];
        }
    };

    // Período anterior imediato, de mesma duração (variação dos tickers). Falha => null (sem variação).
    const fetchPreviousData = async (start, end) => {
        try {
            const response = await httpClient.get(`/dashboard/quote${periodQuery(start, end)}`);
            return response.status >= 200 && response.status < 300 && response.data ? response.data : null;
        } catch (error) {
            return null;
        }
    };

    const loadAllData = useCallback(async () => {
        const id = ++requestId.current;
        setLoading(true);
        setPrevData(null);

        // Variação vs. período anterior em paralelo, sem bloquear o loading principal.
        if (!isMobileShell && startDate?.isValid?.() && endDate?.isValid?.()) {
            const days = endDate.startOf('day').diff(startDate.startOf('day'), 'day') + 1;
            const prevEnd = startDate.startOf('day').subtract(1, 'day').endOf('day');
            const prevStart = prevEnd.startOf('day').subtract(days - 1, 'day');
            fetchPreviousData(prevStart, prevEnd).then((result) => {
                if (id === requestId.current) setPrevData(result);
            });
        }

        try {
            const [quoteDataResult, quoteVsOrderResult] = await Promise.all([
                fetchQuoteData(startDate, endDate),
                fetchQuoteVsOrderData(startDate, endDate)
            ]);
            if (id !== requestId.current) return;
            setQuoteData(quoteDataResult);
            setQuoteVsOrderData(quoteVsOrderResult);
        } catch (error) {
            console.error('Erro ao carregar dados:', error);
        } finally {
            if (id === requestId.current) setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate, isMobileShell, canChart]);

    const handleRefresh = async () => {
        await loadAllData();
    };

    useEffect(() => {
        loadAllData();
    }, [loadAllData]);

    // No mobile, o refresh vai para o AppBar (e sai do page-head).
    useAppBarAction({
        label: 'Atualizar',
        icon: RefreshCw,
        onClick: handleRefresh,
        disabled: loading,
        spinning: loading,
        enabled: isMobileShell,
    });

    // Único lookup de cor/rótulo/ordem por status de orçamento desta tela —
    // mesmos 5 status que `getChartByStatus` (fv-api/dashboard/quote.go)
    // sempre retorna (union fixa, então nunca falta nenhum), reordenados aqui
    // para bater com a ordem visual fixa do protótipo (a API ordena
    // alfabeticamente). Cores vêm do tema (DESIGN-SYSTEM.md §4).
    const STATUS_META = [
        { key: 'novo', label: 'Novo', color: theme.palette.primary.main, bg: theme.palette.info.bg, icon: <Sparkles size={15} strokeWidth={2} /> },
        { key: 'aguardando aprovacao', label: 'Aguardando Aprovação', color: theme.palette.warning.main, bg: theme.palette.warning.bg, icon: <Clock size={15} strokeWidth={2} /> },
        { key: 'aprovado', label: 'Aprovado', color: theme.palette.success.main, bg: theme.palette.success.bg, icon: <Check size={15} strokeWidth={2} /> },
        { key: 'enviado para cliente', label: 'Enviado', color: theme.palette.secondary.main, bg: theme.palette.secondary.bg, icon: <Send size={15} strokeWidth={2} /> },
        { key: 'ordem gerada', label: 'Ordem Gerada', color: theme.palette.brand2.main, bg: theme.palette.info.bg, icon: <FileCheck size={15} strokeWidth={2} /> },
    ];

    const countByStatusKey = new Map((quoteData.chart_by_status || []).map((item) => [item.label, item.value]));
    const totalQuotes = quoteData.total_quotes || 0;
    const pieData = STATUS_META.map((meta, index) => ({
        id: index,
        value: countByStatusKey.get(meta.key) || 0,
        label: meta.label,
        color: meta.color,
    }));
    const hasStatusData = pieData.some((item) => item.value > 0);
    const statusMax = Math.max(...pieData.map((item) => item.value), 0);

    const barChartData = quoteData.chart_by_day || [];
    const hasBarData = barChartData.some((item) => item.total > 0);
    const hasLineData = (quoteVsOrderData || []).some((item) => item.quotes_count > 0 || item.orders_count > 0);

    // --- Indicadores do período (tickers) ---
    // Séries diárias (valor total / quantidade / valor médio) a partir de `chart_by_day`,
    // que omite dias sem orçamento: preenche zeros dia a dia. Conversão e ordens geradas
    // não têm série diária no backend => sem sparkline.
    const periodDays = startDate?.isValid?.() && endDate?.isValid?.() ? endDate.startOf('day').diff(startDate.startOf('day'), 'day') + 1 : 0;
    const dayList = [];
    if (periodDays > 0 && periodDays <= MAX_SPARK_DAYS) {
        for (let i = 0; i < periodDays; i += 1) dayList.push(startDate.startOf('day').add(i, 'day'));
    }
    const byDayKey = new Map(barChartData.map((item) => [item.day, item]));
    const sparkDays = dayList.length > 1 ? dayList.map((d) => d.format('YYYY-MM-DD')) : undefined;
    const buildSeries = (pick) => (dayList.length > 1 ? dayList.map((d) => pick(byDayKey.get(d.format('DD/MM')))) : undefined);
    const valueSeries = buildSeries((row) => Math.round((row?.total || 0) * 100));
    const countSeries = buildSeries((row) => row?.count || 0);
    const avgSeries = buildSeries((row) => (row && row.count > 0 ? Math.round((row.total / row.count) * 100) : 0));

    const trend = (total, previous, series) => (prevData ? { total, previous_total: previous, series } : undefined);
    const valueTrend = trend(toCents(quoteData.total_amount), prevData && toCents(prevData.total_amount), valueSeries);
    const countTrend = trend(totalQuotes, prevData?.total_quotes || 0, countSeries);
    const avgTrend = trend(toCents(quoteData.average_amount), prevData && toCents(prevData.average_amount), avgSeries);
    // Taxa de conversão = valor do backend (aprovados ÷ total), em pontos-base para o ticker.
    const rateValue = toRate(quoteData.conversion_rate);
    const rateTrend = trend(Math.round(rateValue * 100), prevData && Math.round(toRate(prevData.conversion_rate) * 100));
    const ordersTrend = trend(quoteData.count_order_generated || 0, prevData?.count_order_generated || 0);
    // `total_amount_with_discount` vem de `service_quotes.total_discount_*`: soma do desconto concedido.
    const discountValue = parseMoney(quoteData.total_amount_with_discount);
    const totalValue = parseMoney(quoteData.total_amount);
    const discountTrend = trend(toCents(quoteData.total_amount_with_discount), prevData && toCents(prevData.total_amount_with_discount));
    const discountShare = totalValue > 0 ? Math.min(100, Math.max(0, (discountValue / totalValue) * 100)) : 0;
    const prevDays = periodDays;

    useAppBarSubtitle(isMobileShell ? 'Dashboard' : null);

    if (isMobileShell) {
        const open = (quoteData.count_new || 0) + (quoteData.count_awaiting_approval || 0) + (quoteData.count_sent_to_client || 0);
        const rate = typeof quoteData.conversion_rate === 'number' ? quoteData.conversion_rate : parseFloat(quoteData.conversion_rate) || 0;
        const days = barChartData.map((d) => ({ label: String(d.day || '').split('/')[0], value: d.total }));
        const statusItems = pieData.map((p) => ({ name: p.label, value: p.value, text: `${totalQuotes > 0 ? Math.round((p.value / totalQuotes) * 100) : 0}%` }));
        const weeks = (quoteVsOrderData || []).map((d) => ({ label: String(d.date), value: d.quotes_count }));
        const weeksAlt = (quoteVsOrderData || []).map((d) => ({ value: d.orders_count }));
        return (
            <PageScaffold title="Dashboard de Orçamentos" titlePageTarget="Orçamentos" links={[{ title: "Dashboard", link: "" }]}>
                <KpiGrid first>
                    <Kpi label="Em aberto" icon="quote" value={open.toLocaleString('pt-BR')} loading={loading} />
                    <Kpi grad label="Aprovados" icon="check" value={(quoteData.count_approved || 0).toLocaleString('pt-BR')} delta={{ text: `${rate.toFixed(0)}% conversão`, tone: 'up' }} loading={loading} />
                    <Kpi label="Enviados" icon="send" value={(quoteData.count_sent_to_client || 0).toLocaleString('pt-BR')} loading={loading} />
                    <Kpi label="Valor total" icon="dollar" value={formatMoneyShort(parseMoney(quoteData.total_amount))} loading={loading} />
                </KpiGrid>
                <SecH title="Conversão" />
                <KitCard>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                        <b>{rate.toFixed(0)}%</b>
                        <Box component="span" sx={{ fontSize: 12.5, color: 'text.disabled' }}>{(quoteData.count_approved || 0).toLocaleString('pt-BR')} de {totalQuotes.toLocaleString('pt-BR')}</Box>
                    </Box>
                    <Box sx={{ mt: '8px' }}><ProgressBar percent={rate} /></Box>
                </KitCard>
                <SecH title="Orçamentos por status" />
                <KitCard>{hasStatusData ? <HBars items={statusItems} /> : <KitEmptyNote>Ainda não há orçamentos registrados</KitEmptyNote>}</KitCard>
                <SecH title="Orçamentos por dia" />
                <KitCard>{hasBarData ? <BarsChart items={days} /> : <KitEmptyNote>Nenhum orçamento registrado</KitEmptyNote>}</KitCard>
                {hasLineData && (
                    <>
                        <SecH title="Orçamentos x Ordens" />
                        <KitCard><BarsChart items={weeks} alt={weeksAlt} /></KitCard>
                    </>
                )}
            </PageScaffold>
        );
    }
    return (
        <PageScaffold title="Dashboard de Orçamentos" titlePageTarget="Orçamentos" links={[{
            title: "Dashboard",
            link: ""
        }]}>
            <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                {/* page-head — ícone + título + descrição, com o seletor de período
                   (chips + calendário + atualizar) à direita, sem card. */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                        <Box sx={{
                            width: 38,
                            height: 38,
                            borderRadius: '11px',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            background: (t) => t.palette.gradient.brand,
                        }}>
                            <FileText size={18} strokeWidth={2} />
                        </Box>
                        <Box>
                            <Typography component="h1" data-page-title sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                                Dashboard de Orçamentos
                            </Typography>
                            <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                                Status, conversão e evolução dos orçamentos no período, com variação sobre o período anterior.
                            </Typography>
                        </Box>
                    </Box>

                    <DesktopPeriodSelector
                        start={startDate}
                        end={endDate}
                        onChange={(s, e) => { setStartDate(s); setEndDate(e); }}
                        onRefresh={handleRefresh}
                        loading={loading}
                        dualMonth
                    />
                </Box>

                {/* Indicadores do período — cards ticker (prototipo-dashboard-orcamentos.html). */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', mb: '10px' }}>
                    <Typography component="h3" sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>Indicadores do período</Typography>
                    {prevData && prevDays > 0 && (
                        <Typography sx={{ fontSize: '10.5px', fontWeight: 600, color: 'text.disabled', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Box component="i" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'success.main', display: 'inline-block' }} />
                            {prevDays} {prevDays === 1 ? 'dia' : 'dias'} · vs. {prevDays} {prevDays === 1 ? 'dia anterior' : 'dias anteriores'}
                        </Typography>
                    )}
                </Box>
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
                    gap: '10px',
                    mb: 2,
                    '& > *': { gridColumn: { xs: 'span 12', sm: 'span 6', lg: 'span 3' }, minWidth: 0 },
                    '& > :first-of-type': { gridColumn: { xs: 'span 12', lg: 'span 6' }, gridRow: { lg: 'span 2' } },
                }}>
                    <KpiTickerCard
                        hero large cumulative uid="qvt" kind="money"
                        label="Valor total orçado"
                        icon={<DollarSign size={13} strokeWidth={2} />} iconBg="success.bg" iconFg="success.main"
                        data={valueTrend} days={sparkDays}
                        value={toCents(quoteData.total_amount)} loading={loading}
                        footer={
                            <>
                                <Box title="Descontos concedidos sobre o valor total" sx={{ display: 'flex', height: 8, borderRadius: '6px', overflow: 'hidden', gap: '2px', bgcolor: 'background.subtle', mb: '6px' }}>
                                    <Box sx={{ width: `${discountShare}%`, bgcolor: 'warning.main' }} />
                                </Box>
                                <SummaryRow
                                    icon={<Tag size={13} strokeWidth={2} />} iconBg="warning.bg" iconFg="warning.main"
                                    label={<>Descontos concedidos <Box component="small" sx={{ fontSize: '10.5px', color: 'text.disabled', fontWeight: 500, ml: '2px' }}>{fmtPct(discountValue, totalValue)} do valor total</Box></>}
                                    chip={<KpiDeltaChip kind="money" inverse data={discountTrend} />}
                                    value={<AnimatedMoney value={quoteData.total_amount_with_discount} />}
                                    loading={loading}
                                />
                            </>
                        }
                    />
                    <KpiTickerCard cumulative uid="qqt" kind="units" label="Total de orçamentos" icon={<FileText size={11} strokeWidth={2} />} iconBg="info.bg" iconFg="primary.main" data={countTrend} days={sparkDays} value={totalQuotes} loading={loading} />
                    <KpiTickerCard uid="qav" kind="money" label="Valor médio" icon={<BarChart3 size={11} strokeWidth={2} />} iconBg="info.bg" iconFg="brand2.main" data={avgTrend} days={sparkDays} value={toCents(quoteData.average_amount)} loading={loading} />
                    <KpiTickerCard neutral uid="qrt" kind="pct" label="Taxa de conversão" icon={<Percent size={11} strokeWidth={2} />} iconBg="secondary.bg" iconFg="secondary.main" data={rateTrend} value={Math.round(rateValue * 100)} loading={loading}
                        note={prevData && <><b>{(quoteData.count_approved || 0).toLocaleString('pt-BR')}</b> aprovados de <b>{totalQuotes.toLocaleString('pt-BR')}</b> orçamentos</>} />
                    <KpiTickerCard uid="qor" kind="units" label="Ordens geradas" icon={<FileCheck size={11} strokeWidth={2} />} iconBg="success.bg" iconFg="success.main" data={ordersTrend} value={quoteData.count_order_generated || 0} loading={loading} />
                </Box>

                {/* row2 — Status dos Orçamentos (lista de linhas) / Orçamentos x Ordens. */}
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', lg: '1fr 1.5fr' },
                    gap: 2,
                    mb: 2,
                    '& > *': { minWidth: 0 },
                }}>
                    <Card>
                        <CardContent sx={{ p: 2.25, display: 'flex', flexDirection: 'column', height: '100%' }}>
                            <CardHeadRow
                                icon={<FileText size={18} strokeWidth={2} />}
                                iconBg="info.bg"
                                iconFg="primary.main"
                                title="Status dos Orçamentos"
                                subtitle="Distribuição no período"
                                loading={loading}
                                right={loading ? <Skeleton variant="text" width={48} height={26} /> : (
                                    <>
                                        <Typography sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>
                                            <AnimatedNumber value={totalQuotes} />
                                        </Typography>
                                        <Typography sx={{ fontSize: '10.5px', fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '.04em' }}>orçamentos</Typography>
                                    </>
                                )}
                            />

                            {loading ? (
                                <>
                                    {[1, 2, 3, 4, 5].map((item) => (
                                        <StatusRow key={item} loading />
                                    ))}
                                </>
                            ) : hasStatusData ? (
                                STATUS_META.map((meta, index) => {
                                    const value = pieData[index].value;
                                    return (
                                        <StatusRow
                                            key={meta.key}
                                            icon={meta.icon}
                                            iconBg={meta.bg}
                                            iconFg={meta.color}
                                            label={meta.label}
                                            value={<AnimatedNumber value={value} />}
                                            barWidth={statusMax > 0 ? (value / statusMax) * 100 : 0}
                                            pctLabel={fmtPct(value, totalQuotes)}
                                            barColor={meta.color}
                                        />
                                    );
                                })
                            ) : (
                                <EmptyMiniState
                                    icon={<FileText size={22} strokeWidth={2} />}
                                    title="Nenhum dado disponível"
                                    subtitle="Não há orçamentos no período selecionado"
                                />
                            )}
                        </CardContent>
                    </Card>

                    {/* Orçamentos x Ordens — panel-head (com subtítulo) + LineChart. */}
                    <Card>
                        <CardContent sx={{ p: 2.25 }}>
                            <Box sx={{ mb: 2 }}>
                                {loading ? (
                                    <>
                                        <Skeleton variant="text" width={150} height={22} />
                                        <Skeleton variant="text" width={110} height={16} />
                                    </>
                                ) : (
                                    <>
                                        <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: 'text.primary' }}>
                                            Orçamentos x Ordens
                                        </Typography>
                                        <Typography sx={{ fontSize: '11px', color: 'text.disabled', mt: '2px' }}>
                                            Orçamentos com ordem gerada e ordens de serviço, por mês
                                        </Typography>
                                    </>
                                )}
                            </Box>

                            <Box sx={{ height: 230, width: '100%' }}>
                                {loading ? (
                                    <Skeleton variant="rounded" width="100%" height={210} />
                                ) : hasLineData ? (
                                    <LineChart
                                        height={210}
                                        margin={{ top: 12, right: 12, bottom: 24, left: 8 }}
                                        hideLegend
                                        grid={{ horizontal: true }}
                                        xAxis={[{
                                            scaleType: 'point',
                                            data: quoteVsOrderData.map((item) => item.date),
                                            disableLine: true,
                                            disableTicks: true,
                                            tickLabelStyle: { fontSize: 10.5, fontWeight: 600, fill: theme.palette.text.disabled },
                                        }]}
                                        yAxis={[{ disableLine: true, disableTicks: true }]}
                                        series={[
                                            {
                                                data: quoteVsOrderData.map((item) => item.quotes_count),
                                                label: 'Orçamentos (com ordem gerada)',
                                                color: theme.palette.primary.main,
                                                showMark: true,
                                                valueFormatter: (value) => `${value ?? 0} orçamento(s)`,
                                            },
                                            {
                                                data: quoteVsOrderData.map((item) => item.orders_count),
                                                label: 'Ordens geradas',
                                                color: theme.palette.success.main,
                                                showMark: true,
                                                valueFormatter: (value) => `${value ?? 0} ordem(ns)`,
                                            },
                                        ]}
                                        sx={{
                                            '& .MuiChartsGrid-line': { stroke: theme.palette.divider },
                                            '& .MuiMarkElement-root': { fill: theme.palette.background.paper, strokeWidth: 2 },
                                        }}
                                    />
                                ) : (
                                    <EmptyMiniState
                                        icon={<BarChart3 size={22} strokeWidth={2} />}
                                        title="Nenhum dado disponível"
                                        subtitle="Não há orçamentos ou ordens para comparar no período"
                                    />
                                )}
                            </Box>

                            {!loading && hasLineData && (
                                <Box sx={{ display: 'flex', gap: 2, mt: 1.5, flexWrap: 'wrap' }}>
                                    <ChartLegendItem color={theme.palette.primary.main} label="Orçamentos (com ordem gerada)" />
                                    <ChartLegendItem color={theme.palette.success.main} label="Ordens geradas" />
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Box>

                {/* Orçamentos por Dia — panel-head (com subtítulo) + BarChart. */}
                <Card>
                    <CardContent sx={{ p: 2.25 }}>
                        <Box sx={{ mb: 2 }}>
                            {loading ? (
                                <>
                                    <Skeleton variant="text" width={160} height={22} />
                                    <Skeleton variant="text" width={220} height={16} />
                                </>
                            ) : (
                                <>
                                    <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: 'text.primary' }}>
                                        Orçamentos por Dia
                                    </Typography>
                                    <Typography sx={{ fontSize: '11px', color: 'text.disabled', mt: '2px' }}>
                                        Valor orçado por dia · dias com movimento no período selecionado
                                    </Typography>
                                </>
                            )}
                        </Box>

                        <Box sx={{ height: 220, width: '100%' }}>
                            {loading ? (
                                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: '100%', px: 1 }}>
                                    {[1, 2, 3, 4, 5, 6, 7].map((item) => (
                                        <Skeleton key={item} variant="rectangular" sx={{ flex: 1, borderRadius: '4px 4px 0 0' }} height={70 + (item % 4) * 28} />
                                    ))}
                                </Box>
                            ) : hasBarData ? (
                                <BarChart
                                    height={220}
                                    margin={{ top: 12, right: 8, bottom: 24, left: 8 }}
                                    borderRadius={6}
                                    dataset={barChartData}
                                    categoryGapRatio={0.5}
                                    hideLegend
                                    xAxis={[{
                                        scaleType: 'band',
                                        dataKey: 'day',
                                        disableLine: true,
                                        disableTicks: true,
                                        tickLabelStyle: { fontSize: 10, fontWeight: 600, fill: theme.palette.text.disabled },
                                    }]}
                                    yAxis={[{ disableLine: true, disableTicks: true }]}
                                    series={[{
                                        dataKey: 'total',
                                        label: 'Valor Total',
                                        valueFormatter: (value) => `R$ ${(value ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                                        color: 'url(#quoteDashboardBarGradient)',
                                    }]}
                                    sx={{
                                        '& .MuiChartsAxis-directionY': { display: 'none' },
                                    }}
                                >
                                    <defs>
                                        <linearGradient id="quoteDashboardBarGradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor={theme.palette.brand2.main} />
                                            <stop offset="100%" stopColor={theme.palette.primary.main} />
                                        </linearGradient>
                                    </defs>
                                </BarChart>
                            ) : (
                                <EmptyMiniState
                                    icon={<BarChart3 size={22} strokeWidth={2} />}
                                    title="Nenhum orçamento no período"
                                    subtitle="Ajuste o período acima para visualizar a evolução diária"
                                />
                            )}
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </PageScaffold>
    );
}
